import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { toast } from "sonner";

const STATUSES = ["New", "Read", "Replied", "Closed"];

export default function AdminEnquiries() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  useEffect(() => { document.title = "Enquiries | Sambiva Admin"; }, []);
  const { data: enquiries = [] } = useQuery({ queryKey: ["admin-enquiries"], queryFn: async () => (await api.get("/admin/enquiries")).data });

  const setStatus = async (eq, status) => {
    await api.patch(`/admin/enquiries/${eq.id}`, { status });
    qc.invalidateQueries({ queryKey: ["admin-enquiries"] });
    if (selected?.id === eq.id) setSelected({ ...eq, status });
    toast.success(`Marked as ${status}.`);
  };

  return (
    <AdminLayout title="Enquiries">
      <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-[#f6f7f9] text-sambiva-muted text-left">
            <tr><th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Company</th><th className="px-4 py-3 font-semibold">Email</th><th className="px-4 py-3 font-semibold">Subject</th><th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3 font-semibold">Status</th></tr>
          </thead>
          <tbody>
            {enquiries.map((e) => (
              <tr key={e.id} data-testid={`enquiry-row-${e.id}`} onClick={() => { setSelected(e); if (e.status === "New") setStatus(e, "Read"); }} className="border-t border-black/5 hover:bg-sambiva-cream/30 cursor-pointer">
                <td className="px-4 py-3 font-medium text-sambiva-charcoal">{e.full_name}</td>
                <td className="px-4 py-3 text-sambiva-muted">{e.company || "—"}</td>
                <td className="px-4 py-3 text-sambiva-muted">{e.email}</td>
                <td className="px-4 py-3 text-sambiva-muted">{e.subject}</td>
                <td className="px-4 py-3 text-sambiva-muted">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {enquiries.length === 0 && <div className="p-10 text-center text-sambiva-muted">No enquiries yet.</div>}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="enquiry-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-sambiva-charcoal">{selected.subject}</h3><StatusBadge status={selected.status} /></div>
            <div className="text-sm space-y-1 mb-4 text-sambiva-muted"><p><b className="text-sambiva-charcoal">{selected.full_name}</b> {selected.company && `· ${selected.company}`}</p><p>{selected.email} {selected.phone && `· ${selected.phone}`}</p></div>
            <p className="text-sm text-sambiva-charcoal bg-sambiva-cream rounded-xl p-4 mb-5 leading-relaxed">{selected.message}</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`enquiry-status-${s}`} onClick={() => setStatus(selected, s)} className={`text-sm px-3 py-1.5 rounded-full font-medium ${selected.status === s ? "bg-sambiva-green text-white" : "bg-sambiva-cream text-sambiva-charcoal"}`}>{s}</button>
              ))}
              <a href={`mailto:${selected.email}`} className="text-sm px-3 py-1.5 rounded-full font-medium bg-sambiva-red text-white ml-auto">Reply by email</a>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
